import { Box, Typography, Stack, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MoreVert, Edit, Delete, Add, Launch } from '@mui/icons-material';
import { baseNewAndDetailClasses, MetrcLogo, roomDetailClasses, errFeedbackFn } from '../../../../utility';
import apiService from '../../../../../api/apiService';
import { getFrtlAliasString } from '../../../../../utility/frtlUtils';
import { FRTLContext } from '../../../../../context/FRTLContext';
import DisplayNestedTable from '../../../../utility/DisplayNestedTable';
import { packagesKeyTitleMap, subPackagesKeyTitleMap } from '../../../utils/TableTitles';
import CreateSourcePackagePopup from '../../Popup/CreateSourcePackagePopup';
import CreateSubPackagePopup from '../../Popup/CreateSubPackagePopup';
import { FeedbackContext } from '../../../../../App';
import { fetchFrtlData } from '../../../../../api/apiUtils';
import _LabSampleHistoryPopup from '../_LabSampleHistoryPopup';

const ActionsDropdown = ({ packageData, onEdit, onDelete, onCreateSubpackage, cropPlanPerms }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(packageData);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(packageData);
    handleClose();
  };

  const handleCreateSubpackage = () => {
    onCreateSubpackage(packageData);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id="actions-button"
        color="inherit"
        aria-controls={open ? 'actions-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVert />
      </IconButton>
      <Menu
        id="actions-menu"
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEdit} disabled={!cropPlanPerms?.update}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={!cropPlanPerms?.update}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCreateSubpackage} disabled={!cropPlanPerms?.update}>
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Subpackage</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const SubPackageActionsDropdown = ({ subPackageData, parentPackageData, onEdit, onDelete, cropPlanPerms }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(subPackageData, parentPackageData);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(subPackageData, parentPackageData);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id="subpackage-actions-button"
        color="inherit"
        aria-controls={open ? 'subpackage-actions-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVert />
      </IconButton>
      <Menu
        id="subpackage-actions-menu"
        MenuListProps={{
          'aria-labelledby': 'subpackage-actions-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEdit} disabled={!cropPlanPerms?.update}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={!cropPlanPerms?.update}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export const PackageStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ONHOLD: 'onhold',
  INTRANSIT: 'intransit',
};

export const PackageStatusLabels = {
  [PackageStatus.ACTIVE]: 'Active',
  [PackageStatus.INACTIVE]: 'Inactive',
  [PackageStatus.ONHOLD]: 'On Hold',
  [PackageStatus.INTRANSIT]: 'In Transit',
};

const _PackagesSubTab = props => {
  const { currDarkThemeClass = '', cropPlanPerms, handleErrorWithFeedback, values: parentValues, handleSetValues } = props;
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFRTLFilter, setSelectedFRTLFilter] = useState('All');
  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);
  const feedbackCX = useContext(FeedbackContext);

  const [values, setValues] = useState({
    openCreatePackage: false,
    isEditMode: false,
    currentPackage: null,
    dataInitialized: false,

    openCreateSubPackage: false,
    isSubPackageEditMode: false,
    currentSubPackage: null,
    selectedSourcePackage: null,
  });

  const [labSamplePopupOpen, setLabSamplePopupOpen] = useState(false);
  const [currentLabSamplePackage, setCurrentLabSamplePackage] = useState(null);

  const handleOpenLabSamplePopup = subPkg => {
    setCurrentLabSamplePackage(subPkg);
    setLabSamplePopupOpen(true);
  };

  const handleCloseLabSamplePopup = () => {
    setLabSamplePopupOpen(false);
    setCurrentLabSamplePackage(null);
  };

  const [bucketInstanceData, setBucketInstanceData] = useState({
    items: [],
    unit: [],
    bucket_categories: [],
    harvests: [],
    item_categories: [],
    buckets: [],
  });

  const { cropplanid } = useParams();

  useEffect(() => {
    if (!frtlContextValue) {
      fetchFrtlData()
        .then(data => {
          setFrtlContextValue(data || {});
        })
        .catch(error => {
          console.error('Error loading FRTL data:', error);
          setFrtlContextValue({});
        });
    }
  }, [frtlContextValue, setFrtlContextValue]);

  const fetchInstances = async () => {
    try {
      if (parentValues?.strains?.[0]?.farmstrain) {
        const response = await apiService.fetchInstance(`new/buckets/?strain=${parentValues?.strains?.[0]?.farmstrain}&cropplan=${cropplanid}`);
        setBucketInstanceData(response?.data || { items: [], unit: [], bucket_categories: [], harvests: [], item_categories: [], buckets: [] });
      }
    } catch (error) {
      console.error('Error fetching bucket instances:', error);
    }
  };

  const normalizeDate = date => {
    if (!date) return null; // if empty string, undefined, or null → force null
    return new Date(date).toISOString().split('T')[0]; // format YYYY-MM-DD
  };

  const fetchPackages = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.fetchInstance(`cropplan-packages/?cropplan_id=${cropplanid}`);
      if (response.status === 200) {
        setPackages(response.data || []);
        setValues(prevState => ({
          ...prevState,
          dataInitialized: true,
        }));
        if (handleSetValues) {
          handleSetValues('packages', response.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      if (handleErrorWithFeedback) {
        handleErrorWithFeedback(errFeedbackFn({}))(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    if (!values.dataInitialized) {
      fetchPackages();
    }
  }, [cropplanid, parentValues?.strains?.[0]?.farmstrain]);

  const handlePackageAddRes = res => {
    const { data } = res;
    const updatedPackages = [...packages, data];

    setPackages(updatedPackages);
    setValues(prevState => ({
      ...prevState,
      openCreatePackage: false,
      isEditMode: false,
      currentPackage: null,
    }));

    if (handleSetValues) {
      handleSetValues('packages', updatedPackages);
    }

    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Package created successfully!',
    });
  };

  const handleEditPackageRes = res => {
    const { data } = res;
    const updatedPackages = packages.map(pkg => (pkg.id === data.id ? data : pkg));

    setPackages(updatedPackages);
    setValues(prevState => ({
      ...prevState,
      openCreatePackage: false,
      isEditMode: false,
      currentPackage: null,
    }));

    if (handleSetValues) {
      handleSetValues('packages', updatedPackages);
    }

    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Package updated successfully!',
    });
  };

  const handleDeletePackageRes = deletedPackageId => {
    const updatedPackages = packages.filter(pkg => pkg.id !== deletedPackageId);
    setPackages(updatedPackages);

    if (handleSetValues) {
      handleSetValues('packages', updatedPackages);
    }

    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Package deleted successfully!',
    });
  };

  const handleAddPackage = formValues => {
    const entity = `cropplan-packages/`;
    const finalPayload = {
      ...formValues,
      archived_date: normalizeDate(formValues.archived_date),
    };

    if (values.isEditMode) {
      handleEditPackage(finalPayload);
    } else {
      apiService
        .createInstance(entity, finalPayload)
        .then(handlePackageAddRes)
        .catch(handleErrorWithFeedback(errFeedbackFn({})));
    }
  };

  const handleEditPackage = formValues => {
    const entity = `cropplan-source-packages/${formValues.id}/`;
    const finalPayload = {
      ...formValues,
      archived_date: normalizeDate(formValues.archived_date),
    };

    apiService
      .modifyInstance(entity, finalPayload)
      .then(handleEditPackageRes)
      .catch(handleErrorWithFeedback(errFeedbackFn({})));
  };

  const handleEdit = packageData => {
    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to update data",
      });
      return;
    }

    setValues(state => ({
      ...state,
      openCreatePackage: true,
      isEditMode: true,
      currentPackage: packageData,
    }));
  };

  const handleDelete = packageData => {
    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to delete data",
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete package ${packageData.metrc_package_tag_number || `ID: ${packageData.id}`}?`)) {
      apiService
        .deleteInstance(`cropplan-packages/${packageData.id}/`)
        .then(() => handleDeletePackageRes(packageData.id))
        .catch(handleErrorWithFeedback(errFeedbackFn({})));
    }
  };

  const handleSubPackageAddRes = res => {
    const { data } = res;
    // Find the parent package and add the subpackage to it
    const updatedPackages = packages.map(pkg => {
      if (pkg.id === values.selectedSourcePackage?.id) {
        return {
          ...pkg,
          subpackages: [...(pkg.subpackages || []), data],
        };
      }
      return pkg;
    });

    setPackages(updatedPackages);
    setValues(prevState => ({
      ...prevState,
      openCreateSubPackage: false,
      isSubPackageEditMode: false,
      currentSubPackage: null,
      selectedSourcePackage: null,
    }));

    if (handleSetValues) {
      handleSetValues('packages', updatedPackages);
    }

    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Subpackage created successfully!',
    });
  };

  const handleEditSubPackageRes = res => {
    const { data } = res;
    // Find the parent package and update the subpackage
    const updatedPackages = packages.map(pkg => {
      if (pkg.id === values.selectedSourcePackage?.id) {
        return {
          ...pkg,
          subpackages: pkg.subpackages?.map(subPkg => (subPkg.id === data.id ? data : subPkg)) || [],
        };
      }
      return pkg;
    });

    setPackages(updatedPackages);
    setValues(prevState => ({
      ...prevState,
      openCreateSubPackage: false,
      isSubPackageEditMode: false,
      currentSubPackage: null,
      selectedSourcePackage: null,
    }));

    if (handleSetValues) {
      handleSetValues('packages', updatedPackages);
    }

    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Subpackage updated successfully!',
    });
  };

  const handleDeleteSubPackageRes = (deletedSubPackageId, parentPackageId) => {
    const updatedPackages = packages.map(pkg => {
      if (pkg.id === parentPackageId) {
        return {
          ...pkg,
          subpackages: pkg.subpackages?.filter(subPkg => subPkg.id !== deletedSubPackageId) || [],
        };
      }
      return pkg;
    });

    setPackages(updatedPackages);

    if (handleSetValues) {
      handleSetValues('packages', updatedPackages);
    }

    feedbackCX.setContextValue(true, {
      type: 'success',
      message: 'Subpackage deleted successfully!',
    });
  };

  const handleAddSubPackage = formValues => {
    const entity = `cropplan-sub-packages/`;
    const finalPayload = {
      ...formValues,
      archived_date: normalizeDate(formValues.archived_date),
    };

    if (values.isSubPackageEditMode) {
      handleEditSubPackage(finalPayload);
    } else {
      apiService
        .createInstance(entity, finalPayload)
        .then(handleSubPackageAddRes)
        .catch(handleErrorWithFeedback(errFeedbackFn({})));
    }
  };

  const handleEditSubPackage = payload => {
    const entity = `cropplan-sub-packages/${payload.id}/`;
    apiService
      .modifyInstance(entity, payload)
      .then(handleEditSubPackageRes)
      .catch(handleErrorWithFeedback(errFeedbackFn({})));
  };

  // Update the existing handleCreateSubpackage function
  const handleCreateSubpackage = packageData => {
    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to create data",
      });
      return;
    }

    setValues(state => ({
      ...state,
      openCreateSubPackage: true,
      isSubPackageEditMode: false,
      currentSubPackage: null,
      selectedSourcePackage: packageData,
    }));
  };

  // Update the existing handleSubPackageEdit function
  const handleSubPackageEdit = (subPackageData, parentPackageData) => {
    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to update data",
      });
      return;
    }

    setValues(state => ({
      ...state,
      openCreateSubPackage: true,
      isSubPackageEditMode: true,
      currentSubPackage: subPackageData,
      selectedSourcePackage: parentPackageData,
    }));
  };

  const handleSubPackageDelete = (subPackageData, parentPackageData) => {
    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to delete data",
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete subpackage ${subPackageData.metrc_sub_package_tag_number || `ID: ${subPackageData.id}`}?`)) {
      apiService
        .deleteInstance(`cropplan-sub-packages/${subPackageData.id}/`)
        .then(() => handleDeleteSubPackageRes(subPackageData.id, parentPackageData.id))
        .catch(handleErrorWithFeedback(errFeedbackFn({})));
    }
  };

  const handleCreatePackages = () => {
    if (!cropPlanPerms?.update) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: "You don't have permissions to create data",
      });
      return;
    }

    setValues(state => ({
      ...state,
      openCreatePackage: true,
      isEditMode: false,
      currentPackage: null,
    }));
  };

  const harvestsMap = useMemo(() => {
    const map = {};
    bucketInstanceData.harvests?.forEach(harvest => {
      map[harvest.id] = harvest.harvest_name;
    });
    return map;
  }, [bucketInstanceData.harvests]);

  const unitsMap = useMemo(() => {
    const map = {};
    bucketInstanceData.unit?.forEach(unit => {
      map[unit.id] = unit.unit_name;
    });
    return map;
  }, [bucketInstanceData.unit]);

  const itemsMap = useMemo(() => {
    const map = {};
    bucketInstanceData.items?.forEach(item => {
      map[item.id] = item.item_name;
    });
    return map;
  }, [bucketInstanceData.items]);

  const bucketsMap = useMemo(() => {
    const map = {};
    bucketInstanceData.buckets?.forEach(bucket => {
      map[bucket.id] = bucket.bucket_name || bucket.name || `Bucket ${bucket.id}`;
    });
    return map;
  }, [bucketInstanceData.buckets]);

  const filteredPackages = useMemo(() => {
    if (selectedFRTLFilter === 'All') {
      return packages;
    }
    return packages.filter(pkg => {
      const pkgFrtl = pkg.frtl || 'N/A';
      return pkgFrtl === selectedFRTLFilter;
    });
  }, [packages, selectedFRTLFilter]);

  const processPackageData = useMemo(() => {
    return filteredPackages.map((pkg, index) => [
      index + 1,
      pkg.metrc_package_id ? <MetrcLogo /> : '',
      pkg.metrc_package_tag_number || 'N/A',
      getFrtlAliasString(pkg.frtl, frtlContextValue),
      pkg.patient_license_number || 'N/A',
      pkg.package_date ? new Date(pkg.package_date).toLocaleDateString() : 'N/A',
      pkg.original_package_weight
        ? `${parseFloat(pkg.original_package_weight).toLocaleString()} ${unitsMap[pkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'}`
        : '0.00 g',
      pkg.current_package_weight
        ? `${parseFloat(pkg.current_package_weight).toLocaleString()} ${unitsMap[pkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'}`
        : '0.00 g',
      itemsMap[pkg.item] || pkg.item || 'N/A',
      pkg.package_note || 'N/A',
      unitsMap[pkg.metrc_unit_of_measurement] || pkg.metrc_unit_of_measurement || 'N/A',
      pkg.harvests && Array.isArray(pkg.harvests)
        ? pkg.harvests.map(harvestId => harvestsMap[harvestId] || `ID: ${harvestId}`).join(', ') || 'N/A'
        : 'N/A',
      pkg.current_package_status ? PackageStatusLabels[pkg.current_package_status] || pkg.current_package_status : 'N/A',
      // pkg.buckets && Array.isArray(pkg.buckets)
      //   ? pkg.buckets.map(bucketId => bucketsMap[bucketId] || `ID: ${bucketId}`).join(', ') || 'N/A'
      //   : 'N/A',
      pkg.subpackages?.length || 0,
      <ActionsDropdown
        key={`actions-${pkg.id || index}`}
        packageData={pkg}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreateSubpackage={handleCreateSubpackage}
        cropPlanPerms={cropPlanPerms}
      />,
    ]);
  }, [filteredPackages, unitsMap, harvestsMap, itemsMap, bucketsMap, frtlContextValue, cropPlanPerms]);

  const processSubpackageData = (subpackages, parentPackageData) => {
    return subpackages.map((subPkg, idx) => [
      idx + 1,
      subPkg.metrc_sub_package_id ? <MetrcLogo /> : ' - ',
      subPkg.metrc_sub_package_tag_number || 'N/A',
      getFrtlAliasString(subPkg.frtl, frtlContextValue),
      subPkg.sub_package_date ? new Date(subPkg.sub_package_date).toLocaleDateString() : 'N/A',
      subPkg.original_sub_package_weight
        ? `${parseFloat(subPkg.original_sub_package_weight).toLocaleString()} ${
            unitsMap[subPkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'
          }`
        : 'N/A',
      subPkg.current_sub_package_weight
        ? `${parseFloat(subPkg.current_sub_package_weight).toLocaleString()} ${
            unitsMap[subPkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'
          }`
        : 'N/A',
      itemsMap[subPkg.item] || subPkg.item || 'N/A',
      subPkg.sub_package_note || 'N/A',
      <Box display="flex" alignItems="center">
        {subPkg.sub_package_type || 'N/A'}
        {subPkg.subpackages && subPkg.subpackages.length > 0 && (
          <IconButton size="small" onClick={() => handleOpenLabSamplePopup(subPkg)} title="View Lab Samples" sx={{ marginLeft: 1 }}>
            <Launch fontSize="small" color="primary" />
          </IconButton>
        )}
      </Box>,
      subPkg.current_sub_package_status ? PackageStatusLabels[subPkg.current_sub_package_status] || subPkg.current_sub_package_status : 'N/A',
      subPkg.is_finished ? 'Yes' : 'No',
      subPkg.archived_date ? new Date(subPkg.archived_date).toLocaleDateString() : 'N/A',
      unitsMap[subPkg.metrc_unit_of_measurement] || subPkg.metrc_unit_of_measurement || 'N/A',
      <SubPackageActionsDropdown
        key={`subpackage-actions-${subPkg.id || idx}`}
        subPackageData={subPkg}
        parentPackageData={parentPackageData}
        onEdit={handleSubPackageEdit}
        onDelete={handleSubPackageDelete}
        cropPlanPerms={cropPlanPerms}
      />,
    ]);
  };

  const packageTableTitles = useMemo(() => packagesKeyTitleMap.map(e => ({ title: e.title })), []);
  const subPackageTableTitles = useMemo(() => subPackagesKeyTitleMap.map(e => ({ title: e.title })), []);

  const nestedConfig = {
    nestedDataKey: 'subpackages',
    nestedTableTitles: subPackageTableTitles,
    processNestedData: rowIndex => {
      const packageData = filteredPackages[rowIndex];
      if (packageData && packageData.subpackages && packageData.subpackages.length > 0) {
        return processSubpackageData(packageData.subpackages, packageData);
      }
      return [];
    },
    hasNestedData: rowIndex => {
      const packageData = filteredPackages[rowIndex];
      return packageData && packageData.subpackages && packageData.subpackages.length > 0;
    },
  };

  const actionBtnsList = [
    {
      key: 'Create Package',
      isMetrc: false,
      disabled: !cropPlanPerms?.update,
      onClick: handleCreatePackages,
    },
  ].map((e, i) => (
    <Button key={i} disabled={e.disabled} onClick={e.onClick}>
      {e.key}
    </Button>
  ));

  const pActionStackProps = {
    direction: 'row',
    spacing: 2,
    justifyContent: 'flex-end',
    alignItems: 'center',
  };

  const createPackagePopupProps = {
    open: values?.openCreatePackage,
    currDarkThemeClass,
    parentValues,
    cropPlanPerms,
    handleAddPackage,
    isEditMode: values?.isEditMode,
    currentPackage: values?.currentPackage,
    bucketInstanceData,
    onClose: () =>
      setValues(state => ({
        ...state,
        openCreatePackage: false,
        isEditMode: false,
        currentPackage: null,
      })),
  };

  const createSubPackagePopupProps = {
    open: values?.openCreateSubPackage,
    currDarkThemeClass,
    parentValues,
    cropPlanPerms,
    handleAddSubPackage,
    isEditMode: values?.isSubPackageEditMode,
    currentSubPackage: values?.currentSubPackage,
    sourcePackageData: values?.selectedSourcePackage,
    bucketInstanceData,
    onClose: () =>
      setValues(state => ({
        ...state,
        openCreateSubPackage: false,
        isSubPackageEditMode: false,
        currentSubPackage: null,
        selectedSourcePackage: null,
      })),
  };

  const labSampleStatus = {
    submited: 'Submitted',
    not_submitted: 'Not Submitted',
  };

  return (
    <Box className={`${baseNewAndDetailClasses?.tableContainer} ${currDarkThemeClass}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" padding={1} width="100%">
        <Typography className={`${roomDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`}>
          Packages ({filteredPackages.length}){isLoading && <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>Loading...</span>}
        </Typography>
      </Stack>

      <Stack {...pActionStackProps}>{actionBtnsList}</Stack>

      <DisplayNestedTable
        entity="Package"
        currDarkThemeClass={currDarkThemeClass}
        tableTitles={packageTableTitles}
        emptyTableMsg="No packages available"
        toShowAddBtn={false}
        enablePagination={false}
        isLoading={isLoading}
        // Recursive Props
        data={filteredPackages}
        renderRow={(pkg, index) => [
          index + 1,
          pkg.metrc_package_id ? <MetrcLogo /> : '',
          pkg.metrc_package_tag_number || 'N/A',
          getFrtlAliasString(pkg.frtl, frtlContextValue),
          pkg.patient_license_number || 'N/A',
          pkg.package_date ? new Date(pkg.package_date).toLocaleDateString() : 'N/A',
          pkg.original_package_weight
            ? `${parseFloat(pkg.original_package_weight).toLocaleString()} ${unitsMap[pkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'}`
            : '0.00 g',
          pkg.current_package_weight
            ? `${parseFloat(pkg.current_package_weight).toLocaleString()} ${unitsMap[pkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'}`
            : '0.00 g',
          itemsMap[pkg.item] || pkg.item || 'N/A',
          pkg.package_note || 'N/A',
          unitsMap[pkg.metrc_unit_of_measurement] || pkg.metrc_unit_of_measurement || 'N/A',
          pkg.harvests && Array.isArray(pkg.harvests)
            ? pkg.harvests.map(harvestId => harvestsMap[harvestId] || `ID: ${harvestId}`).join(', ') || 'N/A'
            : 'N/A',
          pkg.current_package_status ? PackageStatusLabels[pkg.current_package_status] || pkg.current_package_status : 'N/A',
          pkg.subpackages?.length || 0,
          <ActionsDropdown
            key={`actions-${pkg.id || index}`}
            packageData={pkg}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreateSubpackage={handleCreateSubpackage}
            cropPlanPerms={cropPlanPerms}
          />,
        ]}
        getNestedData={item => {
          // Stop recursion at Level 2
          if (item.sub_package_type) return [];
          return item.subpackages;
        }}
        nestedTableTitles={subPackageTableTitles}
        nestedRenderRow={(subPkg, idx, parentPackageData) => [
          idx + 1,
          subPkg.metrc_sub_package_id ? <MetrcLogo /> : ' - ',
          subPkg.metrc_sub_package_tag_number || 'N/A',
          getFrtlAliasString(subPkg.frtl, frtlContextValue),
          subPkg.sub_package_date ? new Date(subPkg.sub_package_date).toLocaleDateString() : 'N/A',
          subPkg.original_sub_package_weight
            ? `${parseFloat(subPkg.original_sub_package_weight).toLocaleString()} ${
                unitsMap[subPkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'
              }`
            : 'N/A',
          subPkg.current_sub_package_weight
            ? `${parseFloat(subPkg.current_sub_package_weight).toLocaleString()} ${
                unitsMap[subPkg.metrc_unit_of_measurement] === 'Kilograms' ? 'Kg' : 'g'
              }`
            : 'N/A',
          itemsMap[subPkg.item] || subPkg.item || 'N/A',
          // subPkg.sub_package_note || 'N/A',
          <Box display="flex" alignItems="center">
            {subPkg.sub_package_type || 'N/A'}
          </Box>,
          subPkg.current_sub_package_status ? PackageStatusLabels[subPkg.current_sub_package_status] || subPkg.current_sub_package_status : 'N/A',
          subPkg.is_finished ? 'Yes' : 'No',
          subPkg.archived_date ? new Date(subPkg.archived_date).toLocaleDateString() : 'N/A',
          unitsMap[subPkg.metrc_unit_of_measurement] || subPkg.metrc_unit_of_measurement || 'N/A',

          // here TODO: status to show lab sample are used are not
          labSampleStatus.not_submitted,

          subPkg.subpackages && subPkg.subpackages.length > 0 && (
            <IconButton size="small" onClick={() => handleOpenLabSamplePopup(subPkg)} title="View Lab Samples" sx={{ marginLeft: 1 }}>
              <Launch fontSize="small" color="primary" />
            </IconButton>
          ),
          <SubPackageActionsDropdown
            key={`subpackage-actions-${subPkg.id || idx}`}
            subPackageData={subPkg}
            parentPackageData={parentPackageData}
            onEdit={handleSubPackageEdit}
            onDelete={handleSubPackageDelete}
            cropPlanPerms={cropPlanPerms}
          />,
        ]}
      />

      <CreateSourcePackagePopup {...createPackagePopupProps} />
      <CreateSubPackagePopup {...createSubPackagePopupProps} />

      {labSamplePopupOpen && (
        <_LabSampleHistoryPopup
          open={labSamplePopupOpen}
          onClose={handleCloseLabSamplePopup}
          subPackageData={currentLabSamplePackage}
          currDarkThemeClass={currDarkThemeClass}
          unitsMap={unitsMap}
          itemsMap={itemsMap}
        />
      )}
    </Box>
  );
};

export default _PackagesSubTab;
